import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Countdown from 'react-countdown';
import './ProductCard.css';

function GradeBadge({ grade }) {
  return <span className={`grade-badge grade-${grade}`}>{grade}</span>;
}

function CountdownRenderer({ days, hours, minutes, seconds, completed }) {
  if (completed) return <span className="countdown expired">Ended</span>;
  if (days > 0) return <span className="countdown">{days} {days === 1 ? 'day' : 'days'} {hours}h</span>;
  if (hours > 0) return <span className="countdown urgent">{hours}h {minutes}m</span>;
  return <span className="countdown critical">{minutes}m {seconds}s</span>;
}

export default function ProductCard({
  product,
  isWishlisted,
  onToggleWishlist,
  onQuickBid,
  onBidError,
  isAdmin,
  onHide,
  selectable,
  selected,
  onToggleSelect,
  showNotes,
  notesValue,
  onSaveNotes,
  userType = 'individual',
}) {
  const navigate = useNavigate();
  const rawImages = product.images || [];
  const images = typeof rawImages === 'string' ? JSON.parse(rawImages) : rawImages;
  const [imgIndex, setImgIndex] = useState(0);
  const [bidAmount, setBidAmount] = useState('');
  const [notes, setNotes] = useState(notesValue || '');

  useEffect(() => { setNotes(notesValue || ''); }, [notesValue]);

  const handleNotesBlur = () => {
    if ((notesValue || '') !== notes) {
      onSaveNotes && onSaveNotes(product.id, notes);
    }
  };

  const isB2C = userType === 'individual';
  const startingPrice = isB2C ? (product.price_ttc || Math.ceil((product.min_price_euro || 0) * 1.20)) : (product.price_ht || Math.ceil(product.min_price_euro || 0));
  const displayPrice = product.best_bid ? Math.ceil(product.best_bid) : startingPrice;
  const minBid = product.min_bid || startingPrice;


  // Badge logic
  const endDate = product.auction_date_end_france ? new Date(product.auction_date_end_france) : null;
  const hoursLeft = endDate ? (endDate - new Date()) / (1000 * 60 * 60) : null;
  const endsToday = hoursLeft !== null && hoursLeft > 0 && hoursLeft < 24;
  const isNew = product.created_at && (new Date() - new Date(product.created_at)) < 48 * 60 * 60 * 1000;

  const prevImg = (e) => { e.stopPropagation(); setImgIndex(i => i > 0 ? i - 1 : images.length - 1); };
  const nextImg = (e) => { e.stopPropagation(); setImgIndex(i => i < images.length - 1 ? i + 1 : 0); };

  // Touch swipe for mobile
  const touchStart = useRef(null);
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) nextImg(e);
      else prevImg(e);
    }
    touchStart.current = null;
  };

  return (
    <div className={`product-card ${selected ? 'selected' : ''}`}>
      {product.recommended_by_admin && (
        <div className="recommended-tag">Recommended for you</div>
      )}
      <div
        className="card-image"
        onClick={() => navigate(`/products/${product.id}`)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {images[imgIndex] ? (
          <img src={images[imgIndex]} alt={product.clean_title} loading="lazy" />
        ) : (
          <div className="card-no-image">No image</div>
        )}
        {/* Badges disabled — kept for potential re-use
        <div className="card-badges">
          {isNew && <span className="card-badge badge-new">New</span>}
          {endsToday && <span className="card-badge badge-ending">Ends soon</span>}
        </div>
        */}
        {images.length > 1 && (
          <>
            <button className="img-arrow img-arrow-left" onClick={prevImg}>&lsaquo;</button>
            <button className="img-arrow img-arrow-right" onClick={nextImg}>&rsaquo;</button>
            <div className="img-dots">
              {images.map((_, i) => (
                <span key={i} className={`img-dot ${i === imgIndex ? 'active' : ''}`} />
              ))}
            </div>
          </>
        )}
        <button
          className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
          onClick={e => { e.stopPropagation(); onToggleWishlist(product.id); }}
          aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill={isWishlisted ? '#E74C3C' : 'none'}
            stroke={isWishlisted ? '#E74C3C' : 'currentColor'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>
        {isAdmin && (
          <button
            className="admin-hide-btn"
            onClick={e => { e.stopPropagation(); onHide && onHide(product.id); }}
            title="Hide this product"
            aria-label="Hide this product"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </button>
        )}
        {selectable && (
          <label
            className="admin-select-checkbox"
            onClick={e => e.stopPropagation()}
            title="Select to assign to a user's wishlist"
          >
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelect && onToggleSelect(product.id)}
            />
          </label>
        )}
      </div>
      <div className="card-body" onClick={() => navigate(`/products/${product.id}`)}>
        <div className="card-brand">{product.brand || 'Unknown'}</div>
        <div className="card-title">{(product.clean_title || '').replace(new RegExp('^' + (product.brand || '') + '\\s*', 'i'), '')}</div>
        <div className="card-row">
          {product.grade_standardized && <GradeBadge grade={product.grade_standardized} />}
          <div className="card-price">Current bid: &euro;{displayPrice.toLocaleString('fr-FR')}</div>
          {product.auction_date_end_france && (
            <span className="card-closing">Closing in <Countdown date={new Date(product.auction_date_end_france)} renderer={CountdownRenderer} /></span>
          )}
        </div>
      </div>
      <div className="card-min-bid" onClick={e => e.stopPropagation()}>
        <span className="card-min-bid-label">Minimum bid:</span>
        <span className="card-min-bid-value">&euro;{minBid.toLocaleString('fr-FR')}</span>
      </div>
      {/* Quick bid section — disabled, kept for potential re-use
      <div className="card-bid-section" onClick={e => e.stopPropagation()}>
        <div className="card-bid-label">Place your maximum bid (min &euro;{minBid.toLocaleString('fr-FR')})</div>
        <div className="card-bid-row">
          <input className="input card-bid-input" type="number" min={minBid} placeholder={`€${minBid.toLocaleString('fr-FR')}`} value={bidAmount} onChange={e => setBidAmount(e.target.value)} />
          <button className="btn btn-primary btn-sm card-bid-btn" disabled={!bidAmount} onClick={() => { if (parseFloat(bidAmount) < minBid) { onBidError && onBidError(`Minimum bid is €${minBid.toLocaleString('fr-FR')}`); return; } onQuickBid(product.id, parseFloat(bidAmount)); setBidAmount(''); }}>Bid</button>
        </div>
        {bidAmount && parseFloat(bidAmount) < minBid && (<div className="card-bid-error">Minimum bid is &euro;{minBid.toLocaleString('fr-FR')}</div>)}
      </div>
      */}
      {showNotes && (
        <div className="card-notes-section" onClick={e => e.stopPropagation()}>
          <textarea
            className="input card-notes-input"
            placeholder="Your notes…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            rows={2}
          />
        </div>
      )}
    </div>
  );
}
